import { MenuList } from "@mui/material";
import React, { useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
	CustomButton,
	DropDown,
	HorizontalDivider,
	icon,
	IconComponent,
	UserAvatar,
} from "../../../../common/components";
import { MenuItemSelectable } from "../..";
import styles from "./ProfileMenu.module.css";
import {
	environmentSwitch,
	hasPermission,
	isSegFeatureEnabled,
	Permissions,
	SegFlags,
	translate,
} from "../../../../common/providers";
import { languages } from "../../../../common/constants";
import { setFirstTimeLogin, setLanguage, setOpenProductTourWarning } from "../../../../common/slice";
import { ApiService } from "../../../../api";
import { isNonEmptyArray } from "../../../../common/utils";
import { getActivePlayableTours } from "../../../../common/components/product-tour/utils/utils";
import { ESSENTIAL_TOURS } from "../../../../common/components/product-tour/tours";
import { NAV_DROPDOWN_PANEL_PROPS } from "../../drop-down-menus/drop-down-style-props";

const myProfileMenus = { name: "navigation:home.edit-my-profile", to: "/profile" };
const aiConsumption = { name: "navigation:home.ai-consumption" };
const myNotifsPrefs = { name: "navigation:home.notification-settings", to: "/notification-settings" };
const productTour = { name: "navigation:profile-menu.help-button.to-product-tour" };
const academyCenter = { name: "navigation:profile-menu.help-button.to-academy" };
const logout = { name: "navigation:home.logout" };
export default function ProfileMenu({
	menuName,
	onClickLogout = null,
	onClose = null,
	onIsSelected = null,
	onOpen = null,
	onOpenAiConsumption = null,
	onOpenHover = null,
	onOpenProductTourMenu = null,
	onSelect = null,
	open = false,
}) {
	const user = useSelector(({ context }) => context.user);
	const language = useSelector(({ context }) => context.language);
	const project = useSelector(({ context }) => context.project);
	const preventNavigation = useSelector(({ context }) => context.preventNavigation);
	const playableTours = useSelector(({ context }) => context.playableTours);
	const startingProductTourDocumentId = useSelector(({ context }) => context.startingProductTourDocumentId);
	const [openLangMenu, setOpenLangMenu] = useState(false);
	const [openHelpMenu, setOpenHelpMenu] = useState(false);
	const mainAnchorRef = useRef(null);
	const langAnchorRef = useRef(null);
	const helpAnchorRef = useRef(null);
	const dispatch = useDispatch();
	const activePlayableTours = useMemo(
		() => getActivePlayableTours(project?.id, ESSENTIAL_TOURS, playableTours),
		[project, playableTours]
	);
	const handleOpen = () => {
		onOpen(menuName);
	};
	const handleOpenHover = () => {
		onOpenHover(menuName);
	};
	const handleClose = () => {
		onClose();
	};
	const handleSelect = () => {
		if (!onIsSelected(myProfileMenus.to)) {
			onSelect(myProfileMenus);
		}
	};
	const handleSelectNotifsPrefs = () => {
		if (!onIsSelected(myNotifsPrefs.to)) {
			onSelect(myNotifsPrefs);
		}
	};
	const handleSelectProductTour = () => {
		if (startingProductTourDocumentId) {
			onOpenProductTourMenu();
		} else {
			dispatch(setOpenProductTourWarning(true));
		}

		handleClose();
	};
	const handleSelectAcademyCenter = () => {
		window.open("https://academy.aitenders.com", "_blank");
		handleClose();
	};
	const handleOpenHelpMenu = () => setOpenHelpMenu((prev) => !prev);
	const handleOpenLangMenu = () => setOpenLangMenu((prev) => !prev);
	const handleIsSelectedLanguage = (value) => {
		if (value === language.value) {
			return true;
		}
		return false;
	};
	const selectLanguage = (value) => {
		const cancelToken = ApiService.getCancelTokenSource();
		dispatch(setLanguage({ value, cancelToken }));
		dispatch(setFirstTimeLogin(false));
	};
	const handleSelectLanguage = ({ value }) => {
		if (language.value === value) {
			onClose();
			return;
		}
		if (preventNavigation.active) {
			// eslint-disable-next-line
			const confirm = window.confirm(preventNavigation.message);
			if (confirm) {
				selectLanguage(value);
			}
		} else {
			selectLanguage(value);
		}
		onClose();
	};

	// Get display name for avatar
	const displayName = user?.displayName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.email;

	return (
		<>
			<CustomButton
				ref={mainAnchorRef}
				className={styles.profileMenu}
				data-cy="profileMenu_btn"
				data-testid="profileMenu.btn"
				onClick={handleOpen}
				onMouseOver={handleOpenHover}
			>
				<UserAvatar
					isCurrentUser
					className={styles.profileMenu__icon}
					displayName={displayName}
					email={user?.email}
					firstName={user?.firstName}
					lastName={user?.lastName}
					size="sm"
					userId={user?.id}
				/>
			</CustomButton>
			<DropDown ref={mainAnchorRef} open={open} onClose={handleClose} {...NAV_DROPDOWN_PANEL_PROPS}>
				<MenuList className={styles.menuList} data-testid="profileMenu.menu">
					{project && (
						<MenuItemSelectable className={styles.menuItem__name}>{project?.name}</MenuItemSelectable>
					)}
					{environmentSwitch({
						main: (
							<span>
								<MenuItemSelectable
									menuItem={myProfileMenus}
									onIsSelected={onIsSelected}
									onSelect={handleSelect}
								/>
								{!hasPermission(Permissions.PLATFORM_ADMIN) && (
									<MenuItemSelectable
										menuItem={aiConsumption}
										onIsSelected={onIsSelected}
										onSelect={onOpenAiConsumption}
									/>
								)}
								<MenuItemSelectable
									menuItem={myNotifsPrefs}
									onIsSelected={onIsSelected}
									onSelect={handleSelectNotifsPrefs}
								/>
							</span>
						),
						veolia: null,
					})}
					<MenuItemSelectable ref={langAnchorRef} className={styles.menuItem} onSelect={handleOpenLangMenu}>
						<span>{translate("navigation:profile-menu.lang-button")}</span>
						<div className={styles.menuItem__icon}>
							<IconComponent color="var(--grey-600)" icon={icon.faChevronRight} size="xs" />
						</div>
					</MenuItemSelectable>
					<HorizontalDivider />
					<MenuItemSelectable ref={helpAnchorRef} className={styles.menuItem} onSelect={handleOpenHelpMenu}>
						<span>{translate("navigation:profile-menu.help-button")}</span>
						<div className={styles.menuItem__icon}>
							<IconComponent color="var(--grey-600)" icon={icon.faChevronRight} size="xs" />
						</div>
					</MenuItemSelectable>
					<HorizontalDivider />
					<MenuItemSelectable menuItem={logout} onSelect={onClickLogout} />
				</MenuList>
			</DropDown>
			<DropDown
				ref={langAnchorRef}
				open={openLangMenu && open}
				onClose={handleOpenLangMenu}
				{...NAV_DROPDOWN_PANEL_PROPS}
			>
				<MenuList className={styles.subMenuList}>
					{languages.map((menu) => (
						<MenuItemSelectable
							key={menu.value}
							autoTranslate="no"
							className={`${menu.value === language.value && styles.menuItem__active}`}
							menuItem={menu}
							onIsSelected={handleIsSelectedLanguage}
							onSelect={handleSelectLanguage}
						/>
					))}
				</MenuList>
			</DropDown>
			<DropDown
				ref={helpAnchorRef}
				open={openHelpMenu && open}
				{...NAV_DROPDOWN_PANEL_PROPS}
				onClose={handleOpenHelpMenu}
			>
				<MenuList className={styles.subMenuList}>
					{isNonEmptyArray(activePlayableTours) && isSegFeatureEnabled(SegFlags.PRODUCT_TOUR) && (
						<MenuItemSelectable
							className={styles.subItem}
							menuItem={productTour}
							onSelect={handleSelectProductTour}
						/>
					)}
					<MenuItemSelectable
						className={styles.subItem}
						menuItem={academyCenter}
						onSelect={handleSelectAcademyCenter}
					/>
				</MenuList>
			</DropDown>
		</>
	);
}
